import {
  CCI_CHECKLIST_ITEMS,
  CCI_CRITICAL_ITEM_IDS,
  type ChecklistItemStatus,
} from './cciCatalog';
import { isFitnessPassing } from './fitnessCatalog';
import type { FitnessAnswers } from './fitnessCatalog';

export interface InspectionItemState {
  id: string;
  status: ChecklistItemStatus;
  note?: string;
}

export type SeverityResult = 'pass' | 'pass_with_notes' | 'blocked';

export interface EvaluateInspectionInput {
  items: InspectionItemState[];
  declarationAccepted: boolean;
}

export interface EvaluateInspectionResult {
  severityResult: SeverityResult;
  canDeclare: boolean;
  blockingItemIds: string[];
  noteRequiredItemIds: string[];
  incompleteItemIds: string[];
  reasons: string[];
}

/** Evaluate checklist completeness + graded release rules before declaration. */
export function evaluateInspection(input: EvaluateInspectionInput): EvaluateInspectionResult {
  const byId = new Map(input.items.map((i) => [i.id, i]));
  const incompleteItemIds: string[] = [];
  const blockingItemIds: string[] = [];
  const noteRequiredItemIds: string[] = [];
  const reasons: string[] = [];
  let hasNonCriticalFailWithNote = false;

  for (const def of CCI_CHECKLIST_ITEMS) {
    const state = byId.get(def.id);
    if (!state) {
      incompleteItemIds.push(def.id);
      continue;
    }

    if (state.status === 'na') {
      if (!def.allowNa) {
        incompleteItemIds.push(def.id);
        reasons.push(`${def.label}: N/A is not allowed`);
      }
      continue;
    }

    if (state.status === 'unset' || (state.status !== 'pass' && state.status !== 'fail')) {
      incompleteItemIds.push(def.id);
      continue;
    }

    if (state.status === 'fail') {
      if (CCI_CRITICAL_ITEM_IDS.has(def.id)) {
        blockingItemIds.push(def.id);
      } else if (!(state.note?.trim())) {
        noteRequiredItemIds.push(def.id);
      } else {
        hasNonCriticalFailWithNote = true;
      }
    }
  }

  if (incompleteItemIds.length > 0) {
    reasons.push('Complete all checklist items before declaring');
  }
  if (blockingItemIds.length > 0) {
    reasons.push('Critical items failed — contact dispatch; cannot declare or set off');
  }
  if (noteRequiredItemIds.length > 0) {
    reasons.push('Non-critical failures require a note');
  }
  if (!input.declarationAccepted) {
    reasons.push('Declaration checkbox is required');
  }

  const canDeclare =
    incompleteItemIds.length === 0 &&
    blockingItemIds.length === 0 &&
    noteRequiredItemIds.length === 0 &&
    input.declarationAccepted;

  let severityResult: SeverityResult = 'pass';
  if (blockingItemIds.length > 0) {
    severityResult = 'blocked';
  } else if (hasNonCriticalFailWithNote || noteRequiredItemIds.length > 0) {
    severityResult = 'pass_with_notes';
  }

  return {
    severityResult,
    canDeclare,
    blockingItemIds,
    noteRequiredItemIds,
    incompleteItemIds,
    reasons,
  };
}

export interface ShiftInspectionGateRecord {
  id: string;
  inspection_date: string;
  declaration_accepted_at: string | null;
  severity_result: SeverityResult | null;
  confirmed_trip_ids?: string[] | null;
  /** Parsed Fitness to Drive payload from work_fields.fitness */
  fitness?: FitnessAnswers | null;
}

export type DepartBlockReason =
  | 'no_shift_inspection'
  | 'not_declared'
  | 'blocked_severity'
  | 'fitness_incomplete'
  | 'light_confirm_required';

export interface AssertDepartAllowedInput {
  /** Calendar date in business timezone, YYYY-MM-DD */
  today: string;
  tripId: string;
  shiftInspection: ShiftInspectionGateRecord | null;
  /** When true, treat this trip as light-confirmed for this attempt. */
  lightConfirmedForTrip?: boolean;
}

export interface AssertDepartAllowedResult {
  allowed: boolean;
  reason?: DepartBlockReason;
  inspectionId?: string;
}

/** Hard gate for trip Set off (CONF: hybrid + hard block + light confirm per trip). */
export function assertDepartAllowed(input: AssertDepartAllowedInput): AssertDepartAllowedResult {
  const insp = input.shiftInspection;
  if (!insp || insp.inspection_date !== input.today) {
    return { allowed: false, reason: 'no_shift_inspection' };
  }
  if (!insp.declaration_accepted_at) {
    return { allowed: false, reason: 'not_declared', inspectionId: insp.id };
  }
  if (insp.severity_result !== 'pass' && insp.severity_result !== 'pass_with_notes') {
    return { allowed: false, reason: 'blocked_severity', inspectionId: insp.id };
  }
  if (!isFitnessPassing(insp.fitness ?? null)) {
    return { allowed: false, reason: 'fitness_incomplete', inspectionId: insp.id };
  }
  const confirmed = insp.confirmed_trip_ids ?? [];
  if (!input.lightConfirmedForTrip && !confirmed.includes(input.tripId)) {
    return { allowed: false, reason: 'light_confirm_required', inspectionId: insp.id };
  }
  return { allowed: true, inspectionId: insp.id };
}
